import { Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ResponseNotificationService } from "./response-notification.service";
import { isPlatformBrowser } from "@angular/common";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { LoaderService } from "./loader.service";
import {buildBugReportErrorContext} from './bug-report-error-context.factory';

@Injectable({
  providedIn: 'root'
})
export class ResponseErrorsHandlerService {

  private translate: TranslateService;

  constructor(
    private rns: ResponseNotificationService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private injector: Injector,
    private ls: LoaderService
  ) {
  }

  handleFormErrors(form: UntypedFormGroup, response: HttpErrorResponse) {
    if (form && response.error?.violations) {
      for (const violation of response.error.violations) {
        let propertyPath = violation.propertyPath;

        // REFACTORME: This is a temporary workaround. The proper solution is to refactor
        // the form structure to use FormArray for addresses with nested FormGroups containing
        // all address fields including addresstype. This would match the backend structure
        // and allow proper error mapping without this hack.
        if (propertyPath.startsWith('addresses[') && propertyPath.includes('.addresstype')) {
          propertyPath = 'addresses';
        }

        const pathParts = this.parsePath(propertyPath);
        const control = this.resolveControlPath(form, pathParts);

        if (control) {
          control.setErrors({
            custom: violation.message
          });
        } else {
          console.warn('form control not found for property path:', violation.propertyPath);
        }
      }
    }
  }

  private parsePath(path: string): (string | number)[] {
    const parts: (string | number)[] = [];

    path.split('.').forEach(part => {
      const match = part.match(/^(\w+)\[(\d+)\]$/);
      if (match) {
        parts.push(match[1], Number(match[2]));
      } else {
        parts.push(part);
      }
    });

    return parts;
  }

  private resolveControlPath(root: UntypedFormGroup | UntypedFormArray, path: (string | number)[]): AbstractControl | null {
    let control: AbstractControl | null = root;

    for (const segment of path) {
      if (!control) return null;

      if (control instanceof UntypedFormGroup && typeof segment === 'string') {
        control = control.get(segment);
      } else if (control instanceof UntypedFormArray && typeof segment === 'number') {
        control = control.at(segment);
      } else {
        return null;
      }
    }

    return control;
  }

  //TODO: Add Translations
  public handleServerErrors(httpErrorResponse: HttpErrorResponse, requestMethod?: string) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const errorCode = this.extractErrorCode(httpErrorResponse);
    const errorContext = buildBugReportErrorContext(httpErrorResponse, requestMethod, this.router.url);
    this.translate = this.injector.get(TranslateService);
    this.ls.hide();
    switch (httpErrorResponse.status) {
      case 0:
        if (this.extractErrorCode(httpErrorResponse) === 'OFFLINE_CACHE_MISS') {
          break;
        }
        if (!this.isCanceledRequest(httpErrorResponse)) {
          this.rns.emitError(this.translate.instant('app.error.api_connection'), errorCode, errorContext);
        }
        break;
      case 401:
        if (!this.isPublicApiRequest(httpErrorResponse.url)) {
          this.rns.emitError(this.translate.instant('app.error.api_token_expired'), errorCode, errorContext);
          this.router.navigate(['/']);
        }
        break;
      case 403:
        this.rns.emitError(
          this.translateServerMessage(httpErrorResponse, this.translate.instant('app.error.api_forbidden')),
          errorCode,
          errorContext
        );
        break;
      case 404:
        this.rns.emitError(
          this.translateServerMessage(httpErrorResponse, this.translate.instant('app.error.api_not_found')),
          errorCode,
          errorContext
        );
        break;
      case 423:
        const lockedMessage = this.translateServerMessage(httpErrorResponse, this.translate.instant('app.error.locked'));
        const msg = lockedMessage === 'user.registration.closed'
          ? this.translate.instant('app.error.registration_closed')
          : lockedMessage;
        this.rns.emitError(msg, errorCode, errorContext);
        break;
      case 422:
        this.rns.emitError(
          this.translateServerMessage(httpErrorResponse, this.translate.instant('app.error.api_internal')),
          errorCode,
          errorContext
        );
        break;
      case 500:
        this.rns.emitError(this.translate.instant('app.error.api_internal'), errorCode, errorContext);
        break;
    }
  }

  private extractServerMessage(httpErrorResponse: HttpErrorResponse, fallbackMessage: string): string {
    const error = httpErrorResponse.error as any;
    const candidatePaths = [
      'violations.0.message',
      'errors.0.message',
      'hydra:description',
      'detail',
      'message',
      'error_description',
      'error.message',
      'error.detail'
    ];

    for (const path of candidatePaths) {
      const value = this.getNestedValue(error, path);
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }

    return fallbackMessage;
  }

  private translateServerMessage(httpErrorResponse: HttpErrorResponse, fallbackMessage: string): string {
    const message = this.extractServerMessage(httpErrorResponse, fallbackMessage);
    const translatedMessage = this.translate.instant(message);

    return translatedMessage !== message ? translatedMessage : message;
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc: any, part: string) => {
      if (acc === null || acc === undefined) {
        return undefined;
      }
      return acc[part];
    }, obj);
  }

  private extractErrorCode(httpErrorResponse: HttpErrorResponse): string | null {
    const error = httpErrorResponse.error as any;
    const code = error?.code;
    if (typeof code === 'string' || typeof code === 'number') {
      return String(code).slice(0, 64);
    }
    return httpErrorResponse.status ? String(httpErrorResponse.status) : null;
  }

  private isPublicApiRequest(url: string | null): boolean {
    if (!url) {
      return false;
    }
    const publicPrefixes = [
      '/api/feeds',
      '/api/events',
      '/api/marketplace_advertisements',
      '/api/posts',
      '/api/app-configs/current',
      '/api/pages/navigation',
      '/api/pages/resolve/',
      '/api/static_pages'
    ];
    return publicPrefixes.some((prefix) => url.includes(prefix));
  }

  private isCanceledRequest(httpErrorResponse: HttpErrorResponse): boolean {
    const error = httpErrorResponse.error as any;

    const isBrowserProgressEvent =
      typeof ProgressEvent !== 'undefined'
      && error instanceof ProgressEvent
      && error.type === 'abort';

    return isBrowserProgressEvent
      || error?.name === 'AbortError'
      || error?.message === 'canceled';
  }
}
